import { dispatch } from '../index'
import { API, graphqlOperation, Auth, Analytics } from 'aws-amplify'
import { includesAny } from '../../lib/arrayOperations'
import * as Sentry from '@sentry/browser'
import config from '../../configuration/config'
import history from '../../History'
import { StorageService } from '../../lib/StorageService'
import { getLatestAcknowledgements } from '../../components/Admin/Reports/Acknowledgement/Utils'

import {
    APP_TOGGLE_ADMIN_MODE,
    APP_SET_SEARCH,
    APP_UPDATE_ATTRIBUTE,
    HANDLE_CHANGE,
    SET_USER_ATTRIBUTES,
    SET_USER_DATA,
    SHOW_UPDATE_ATTRIBUTES,
    SET_TRAINING_RESULTS,
    UPDATE_WINDOW_DIMENSIONS,
    UPDATE_BUILDING_DATA,
    SHOW_BUILDING_PICKER,
    UPDATE_USER_DATA,
    SET_ADMIN_STATUS,
    APP_SHOW_ERROR_PAGE,
    APP_SET_PLAYLISTS,
    APP_SET_PROPERTY_PLAYLISTS,
    UPDATE_USER_ROLES,
    UPDATE_TRAINING_RESULT,
    SET_USER_OCCUPANCIES,
    SHOW_TERMS_OF_USE,
    SET_TERMS_ACCEPTED
} from '../actionTypes'

const listOccupancy = `query($sk: String, $pk: ID) {
    listOccupancy(sk: $sk, pk: $pk) {
        items {
            pk
            sk
            d1
            d2
            roles
            firstName
            lastName
            email
            workPhone {
                number
                extension
            }
            phone {
                number
                extension
            }
            tenant
            building
            property
        }
    }
}`

const operation = `query ($sk: String, $d2: ID) {
    getOccupancy(sk: $sk, d2: $d2) {
        ... on Occupant {
            pk
            sk
            d1
            d2
            haveLoggedIn
            firstName
            lastName
            roles
            primaryContact
            workPhone {
                number
                extension
            }
            phone {
                number
                extension
            }
            lastLogin
            metaData {
                createdAt
            }
            extraFields {
                key
                value
            }
            email
            version
            termsAcceptanceDate
            __typename
        }
        ... on EvacuationAssistance {
            pk
            sk
            d1
            assistanceNeeded
            email
            createdAt
            endDate
            floor
            firstName
            lastName
            phone {
                number
                extension
            }
            startDate
            suite
            version
            buddies
            __typename
        }
        ... on Building {
            pk
            d1
            name
            version
            settings {
                closedCaption
                requiredFloorWardens
            }
            createdAt
            address {
                city
                country
                name
                region
                state
                street
                zipCode
            }
            assemblyAreas {
                coordinates {
                  latitude
                  longitude
                }
                title
              }
            coordinates {
                latitude
                longitude
            }
            settings {
                dsal
                modules {
                    title
                    enabled
                    key
                }
                phoneNumbers {
                    name
                    number
                }
                dsal
                systemConfig {
                    suites
                    support
                    systemNotifications
                    termsOfUse
                    userFields
                    whiteLabels
                    scheme
                    passwordLinkNeverExpiration
                    termsOfUse
                }
            }
            __typename
        }
        ... on Property {
            pk
            d1
            name
            createdAt
            version
            resources {
                local {
                    title
                    url
                }
                regional {
                    title
                    url
                }
                federal {
                    title
                    url
                }
            }
            settings {
                positions {
                    enabled
                    floorWarden
                    team
                }
                phoneNumbers {
                    name
                    number
                }
                multiBuilding
                suites
                modules {
                    title
                    enabled
                    key
                }
            }
            __typename
        }
        ... on Client {
            pk
            sk
            name
            logo
            color {
                r
                b
                g
            }
            __typename
        }
        ... on Tenant {
            pk
            sk
            d1
            name
            floors
            suites
            metaData { 
                createdAt
            }
            version
            __typename
        }
        ... on TrainingResult {
            certificateType
            completedAt
            createdAt
            d1
            d2
            expiredAt
            firstName
            lastName
            pk
            quizzesCompleted
            sk
            title {
                language
                text
            }
            updatedAt
            userType
            version
            videosCompleted
            __typename
        }
    }
}`

const listTrainingResults = `
    query ListTrainingResults(
        $sk: String!,
        $d2: String,
        $limit: Int,
        $nextToken: String
    ) {
        listTrainingResults(
            sk: $sk,
            d2: $d2,
            limit: $limit,
            nextToken: $nextToken
        ) {
            nextToken
            items {
                certificateType
                completedAt
                createdAt
                d1
                d2
                expiredAt
                firstName
                lastName
                pk
                quizzesCompleted
                sk
                title {
                    language
                    text
                }
                updatedAt
                userType
                version
                videosCompleted
            }
        }
    }
`

export const setSearch = (event) => dispatch({ value: event.target.value, type: APP_SET_SEARCH })
export const handleChange = (name) => (event) => dispatch({ name, event, type: HANDLE_CHANGE })
export const updateAttribute = (attr, val) => dispatch({ attr, val, type: APP_UPDATE_ATTRIBUTE })
export const updateBuildingData = (data) => dispatch({ data, type: UPDATE_BUILDING_DATA })

export const setTrainingResults = trainingResults => dispatch({ trainingResults, type: SET_TRAINING_RESULTS })
export const updateTrainingResult = newResult => dispatch({ newResult, type: UPDATE_TRAINING_RESULT })
export const updateWindowDimensions = (width, height) => dispatch({ width, height, type: UPDATE_WINDOW_DIMENSIONS })
export const showUserConfigurator = (show) => dispatch({ show: show, type: SHOW_UPDATE_ATTRIBUTES })
export const updateUser = (user) => dispatch({ data: user, type: UPDATE_USER_DATA })
export const setUserAttributes = (user) => dispatch({ user, data: user.attributes, type: SET_USER_ATTRIBUTES })
export const setPlaylists = (playlists) => dispatch({ playlists, type: APP_SET_PLAYLISTS })
export const setPropertyPlaylists = (playlists) => dispatch({ playlists, type: APP_SET_PROPERTY_PLAYLISTS })
export const updateUserRoles = (roles) => dispatch({ roles, type: UPDATE_USER_ROLES })

export const showBuildingPicker = (show) => dispatch({ show, type: SHOW_BUILDING_PICKER })
export const showErrorPage = (show) => dispatch({ show, type: APP_SHOW_ERROR_PAGE })

export const setTermsAccepted = (accepted) => dispatch({
    type: SET_TERMS_ACCEPTED,
    payload: accepted
})

export const showTermsOfUse = (show) => dispatch({
    type: SHOW_TERMS_OF_USE,
    payload: show
})

export const getUserData = () => {
    Auth.currentAuthenticatedUser().then((user) => {
        dispatch({ user, data: user.attributes, type: SET_USER_ATTRIBUTES })

        Sentry.configureScope((scope) => {
            scope.setUser({ email: user.attributes.email })
        })

        // Check if this is a page refresh
        const isPageRefresh = !window.performance ||
            window.performance.navigation.type === window.performance.navigation.TYPE_RELOAD;

        // Check if we have a stored building selection in localStorage
        const savedBuilding = StorageService.get('currentBuilding');

        // If this is a page refresh and we have a saved building, try to use it directly
        if (isPageRefresh && savedBuilding) {
            // Try to reuse the saved building data on page refresh
            getProgramData(savedBuilding, user);
            // We'll still fetch occupancies but won't show the building picker
        }

        // Always fetch the latest occupancies
        API.graphql(graphqlOperation(listOccupancy, { pk: user.username, sk: 'OCCUPANCY' }))
            .then(res => {
                let occupancy = res.data.listOccupancy.items;

                dispatch({
                    data: occupancy,
                    type: SET_USER_OCCUPANCIES
                });

                // If we already used a saved building from localStorage and we're on a page refresh,
                // don't show the building picker - we're already in the right building context
                if (isPageRefresh && savedBuilding) {
                    // No need to show the building picker or reprocess the building data
                    return;
                }

                // If we're not refreshing or don't have a saved building, 
                // handle the building selection logic
                if (occupancy.length > 1 && (!savedBuilding || !isPageRefresh)) {
                    // Show building picker if user has multiple buildings and no saved selection
                    // or if this is not a page refresh (e.g., initial load)
                    showBuildingPicker(true);
                } else if (occupancy.length > 1 && savedBuilding && isPageRefresh) {
                    // Try to find the matching occupancy for the saved building
                    const matchedOccupancy = occupancy.find(o =>
                        (o.pk === savedBuilding.pk && o.sk === savedBuilding.sk) ||
                        (o.d2 === savedBuilding.d2) ||
                        (o.building === savedBuilding.building && o.tenant === savedBuilding.tenant)
                    );

                    if (matchedOccupancy) {
                        // Use the matched occupancy data
                        getProgramData(matchedOccupancy, user);
                    } else {
                        // If no match is found, show the building picker
                        showBuildingPicker(true);
                    }
                } else if (occupancy.length === 1) {
                    // User has only one building, use it
                    getProgramData(occupancy[0], user);
                } else if (occupancy.length === 0) {
                    // No occupancies found
                    console.error('No occupancies found for user');
                    showErrorPage(true);
                }
            });
    });
}

export const getProgramData = async (data, user) => {
    // Always hide the building picker when we have data
    showBuildingPicker(false)

    // Also save the current building data in localStorage if not already saved
    // This ensures we have the data available on page refresh
    if (!data.__wasExplicitlySelected) {
        const savedData = {
            ...data,
            __wasExplicitlySelected: true
        };
        StorageService.set('currentBuilding', savedData);
    }

    const response = await API.graphql(graphqlOperation(operation, {
        d2: data.d2
    }))

    if (response.errors) {
        console.log("getProgramData: ", response.errors)
        Sentry.captureException(response.errors[0].message)
        showErrorPage(true)
        return null
    }

    let _property, _client, _occupant, _tenant, _evac, _building
    let _trainingResults = []

    response.data.getOccupancy.forEach((d) => {
        switch (d.__typename) {
            case 'Property':
                _property = d
                break
            case 'Client':
                _client = d
                break
            case 'Occupant':
                _occupant = d
                break
            case 'Tenant':
                _tenant = d
                break
            case 'EvacuationAssistance':
                _evac = d
                break
            case 'Building':
                _building = d
                break
            case 'TrainingResult':
                _trainingResults.push(d)
                break
            default:
                break
        }
    })

    // Fetch training results from all occupancies in the same building
    // This ensures users with multiple tenants see consistent training status
    try {
        // Get all occupancies for this user
        const occupanciesResponse = await API.graphql(graphqlOperation(listOccupancy, {
            pk: _occupant.pk,
            sk: 'OCCUPANCY'
        }))

        const allOccupancies = occupanciesResponse.data.listOccupancy.items || []

        // Filter for occupancies in the same building
        const buildingId = _occupant.d1.split('#')[3]
        const sameBuildingOccupancies = allOccupancies.filter(occ => {
            const occBuildingId = occ.d1.split('#')[3]
            return occBuildingId === buildingId && occ.d2 !== data.d2
        })

        // Fetch training results for each occupancy in the same building
        if (sameBuildingOccupancies.length > 0) {
            const trainingPromises = sameBuildingOccupancies.map(occ =>
                API.graphql(graphqlOperation(operation, { d2: occ.d2 }))
                    .then(res => {
                        const results = res.data.getOccupancy.filter(item => item.__typename === 'TrainingResult')
                        return results
                    })
                    .catch(err => {
                        console.error('Error fetching training results for d2:', occ.d2, err)
                        return []
                    })
            )

            const additionalResultsArrays = await Promise.all(trainingPromises)
            const additionalResults = additionalResultsArrays.flat()
            _trainingResults.push(...additionalResults)
        }
    } catch (error) {
        console.error('Error fetching additional training results:', error)
        // Continue with just the current occupancy's results
    }

    const floor = _occupant.d1.split('#')[5] || null
    const suite = _occupant.d1.split('#')[6] || null

    dispatch({
        data: {
            tenant: _tenant,
            building: _building,
            property: _property,
            client: _client,
            floor,
            suite,
            userId: user.username,
            ..._occupant
        }, type: SET_USER_DATA
    })

    // Store all occupancies in Redux for use in sync operations
    try {
        const occupanciesResponse = await API.graphql(graphqlOperation(listOccupancy, {
            pk: _occupant.pk,
            sk: 'OCCUPANCY'
        }))
        const allOccupancies = occupanciesResponse.data.listOccupancy.items || []
        dispatch({ data: allOccupancies, type: SET_USER_OCCUPANCIES })
    } catch (error) {
        console.error('Error fetching occupancies:', error)
    }

    checkIfAdmin(_occupant)
    setTrainingResults(_trainingResults)

    // Check if this is a page refresh (no navigation history or direct URL access)
    const isPageRefresh = !window.performance ||
        window.performance.navigation.type === window.performance.navigation.TYPE_RELOAD;

    // Only redirect if this is NOT a page refresh OR we're switching buildings OR it's a tenant admin
    const isDirectURLAccess = !window.location.href.includes('?') && window.location.pathname === '/';
    const isTenantAdmin = _occupant?.roles?.includes('TENANT_ADMIN');
    const fromBuildingSelector = data.__fromBuildingSelector;

    // FIXED: Prevent dashboard flashing for registration link users with multiple associations
    // Don't redirect immediately if this data comes from registration link processing
    // Let the building picker handle the routing instead
    const shouldRedirect = (!isPageRefresh || fromBuildingSelector || isTenantAdmin);

    // Route users to appropriate pages
    if (shouldRedirect && (_occupant && _occupant.roles)) {
        const isPropertyAdmin = _occupant.roles.includes('PROPERTY_ADMIN')
        const isBuildingAdmin = _occupant.roles.includes('BUILDING_ADMIN')
        const isSuperAdmin = _occupant.roles.includes('SUPER_ADMIN')
        const isAdmin = isPropertyAdmin || isBuildingAdmin || isTenantAdmin || isSuperAdmin

        // Route according to the highest level role
        if (isSuperAdmin) {
            // Super admin should always go to dashboard
            history.push('/dashboard')
        } else if (isPropertyAdmin && _property) {
            history.push(`/admin/properties/${_property.pk}`)
        } else if (isBuildingAdmin && _building) {
            history.push(`/admin/buildings/${_building.pk}`)
        } else if (isTenantAdmin && _tenant) {
            // For tenant admin, check if they need to see the activation policy first
            // We need to check if tenant acknowledgments exist
            const checkTenantAcknowledgement = async () => {
                try {
                    const tenantAckQuery = `query GetTenant($pk: ID!, $sk: String) {
                        getTenant(pk: $pk, sk: $sk) {
                            ... on TenantAcknowledgements {
                                __typename
                                pk
                                sk
                                d1
                                building
                                tenant
                                acknowledgements {
                                    acknowledgedBy {
                                        pk
                                        firstName
                                        lastName
                                        email
                                    }
                                    active
                                    declined
                                    timestamp
                                }
                            }
                        }
                    }`

                    const response = await API.graphql(graphqlOperation(tenantAckQuery, {
                        pk: _tenant.pk,
                        sk: 'TENANT_ACKNOWLEDGEMENTS'
                    }))

                    let acknowledgementData = response.data.getTenant

                    // Handle case where response might be an array (shouldn't happen but being defensive)
                    if (Array.isArray(acknowledgementData)) {
                        acknowledgementData = acknowledgementData[0]
                    }

                    if (acknowledgementData) {
                        const latestAck = getLatestAcknowledgements(acknowledgementData)

                        // Determine if we should show the acknowledgment form
                        let shouldShowDashboard = false

                        if (!latestAck) {
                            // No acknowledgments exist, show dashboard (policy form will appear)
                            shouldShowDashboard = true
                        } else if (latestAck.declined) {
                            // Latest acknowledgment was declined, show dashboard (policy form will appear)
                            shouldShowDashboard = true
                        } else if (!latestAck.active) {
                            // Latest acknowledgment is not active, show dashboard (policy form will appear)
                            shouldShowDashboard = true
                        } else {
                            // Latest acknowledgment is active (accepted)

                            // If the latest acknowledgment was accepted by this user, go to admin dashboard
                            if (latestAck.acknowledgedBy?.pk === _occupant.pk) {
                                shouldShowDashboard = false
                            } else {
                                // Latest acknowledgment was accepted by someone else
                                // Check if this user has declined before
                                if (acknowledgementData && acknowledgementData.acknowledgements) {
                                    const userDeclinedAcks = acknowledgementData.acknowledgements.filter(ack =>
                                        ack.acknowledgedBy?.pk === _occupant.pk && ack.declined
                                    )

                                    if (userDeclinedAcks.length > 0) {
                                        // This user has declined before, check if someone else accepted after the decline
                                        const userLatestDecline = userDeclinedAcks.reduce((latest, current) =>
                                            (!latest.timestamp || current.timestamp > latest.timestamp) ? current : latest
                                        )

                                        // If the latest overall acknowledgment is accepted and happened after this user's decline
                                        if (latestAck.active && !latestAck.declined && latestAck.timestamp > userLatestDecline.timestamp) {
                                            // Another admin accepted after this user declined, go to admin dashboard
                                            shouldShowDashboard = false
                                        } else {
                                            // This user declined and no one else has accepted since, show dashboard
                                            shouldShowDashboard = true
                                        }
                                    } else {
                                        // This user hasn't declined, but someone else has accepted, go to admin dashboard
                                        shouldShowDashboard = false
                                    }
                                } else {
                                    // No acknowledgment data, but latestAck exists - should not happen, but be safe
                                    shouldShowDashboard = false
                                }
                            }
                        }

                        if (shouldShowDashboard) {
                            // User needs to see the tenant activation policy, route to dashboard
                            const isDashboardEnabled = _building?.settings?.modules?.some(
                                module => module.key === 'dashboard' && module.enabled
                            )
                            if (isDashboardEnabled) {
                                history.push('/dashboard')
                            } else {
                                history.push('/trainingportal')
                            }
                        } else {
                            // Policy is accepted, can go to admin dashboard
                            history.push(`/admin/tenants/${_tenant.pk}`)
                        }
                    } else {
                        // No acknowledgment data, route to dashboard to be safe
                        const isDashboardEnabled = _building?.settings?.modules?.some(
                            module => module.key === 'dashboard' && module.enabled
                        )
                        if (isDashboardEnabled) {
                            history.push('/dashboard')
                        } else {
                            history.push('/trainingportal')
                        }
                    }
                } catch (error) {
                    console.error('Error checking tenant acknowledgment:', error)
                    // On error, route to dashboard to be safe
                    const isDashboardEnabled = _building?.settings?.modules?.some(
                        module => module.key === 'dashboard' && module.enabled
                    )
                    if (isDashboardEnabled) {
                        history.push('/dashboard')
                    } else {
                        history.push('/trainingportal')
                    }
                }
            }

            checkTenantAcknowledgement()
        } else if (!isAdmin) {
            // For non-admin users (occupants)
            // Check if dashboard module is enabled
            const isDashboardEnabled = _building?.settings?.modules?.some(
                module => module.key === 'dashboard' && module.enabled
            )

            if (isDashboardEnabled) {
                // If dashboard is enabled, direct to dashboard
                history.push('/dashboard')
            } else {
                // Otherwise, direct to training portal
                history.push('/trainingportal')
            }
        }
    } else if (isDirectURLAccess) {
        // If visiting root URL directly (not refresh), redirect to training portal
        history.push('/trainingportal')
    }
}

const checkIfAdmin = (user) => {
    let roles = user.roles
    const isAdmin = includesAny(roles, ['SUPER_ADMIN', 'PROPERTY_ADMIN', 'BUILDING_ADMIN', 'TENANT_ADMIN'])
    if (isAdmin) dispatch({ admin: true, type: SET_ADMIN_STATUS })
    return isAdmin
}
